import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStar,
  faPaw,
  faTree,
  faLandmark,
  faAnchor,
  faClock,
  faSun,
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import heroImage from '../../../assets/banner/playBan.webp';

const Page = styled.div`
  min-height: 100vh;
  padding-top: 120px;
  background-color: ${props => props.theme.colors.offWhite};
`;

const EventHero = styled.div`
  width: 100%;
  height: 420px;
  background-image: ${props => `linear-gradient(to bottom, rgba(26, 58, 82, 0.5), rgba(26, 58, 82, 0.75)), url(${props.image})`};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  position: relative;

  @media (max-width: 768px) {
    height: 360px;
  }
`;

const HeroContent = styled.div`
  width: 100%;
  max-width: 1400px;
  padding: 3rem 2rem;
  color: white;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const Badge = styled.span`
  display: inline-block;
  background: linear-gradient(135deg, #1a3a52 0%, #2c5f7f 100%);
  color: #f4d03f;
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  border: 2px solid #f4d03f;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 3rem;
  font-weight: 700;
  margin: 0.25rem 0 0.5rem;
  line-height: 1.2;
  text-shadow: 0 3px 12px rgba(0,0,0,0.6);

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const Strapline = styled.p`
  font-size: 1.25rem;
  margin: 0;
  opacity: 0.95;
  text-shadow: 0 2px 8px rgba(0,0,0,0.5);

  @media (max-width: 768px) {
    font-size: 1.05rem;
  }
`;

const Content = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 2rem 4rem;
`;

const IntroSection = styled.div`
  text-align: center;
  margin-bottom: 2.5rem;
`;

const IntroText = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  max-width: 700px;
  margin: 0 auto 1rem;
`;

const GroupSaverBox = styled.div`
  background: linear-gradient(135deg, rgba(26, 58, 82, 0.08) 0%, rgba(26, 58, 82, 0.12) 100%);
  border-left: 4px solid #1a3a52;
  padding: 1.25rem 1.5rem;
  border-radius: 10px;
  margin: 1.5rem auto 2rem;
  max-width: 700px;
  text-align: center;

  p {
    margin: 0;
    font-size: 1.1rem;
    line-height: 1.7;
    color: ${props => props.theme.colors.primary};
  }

  strong {
    color: #1a3a52;
  }
`;

const CircusBonus = styled.div`
  background: linear-gradient(135deg, rgba(244, 208, 63, 0.12) 0%, rgba(244, 208, 63, 0.18) 100%);
  border-left: 4px solid #f4d03f;
  padding: 1.25rem 1.5rem;
  border-radius: 10px;
  margin: 1.5rem auto 2rem;
  max-width: 700px;
  text-align: center;

  p {
    margin: 0;
    font-size: 1.1rem;
    line-height: 1.7;
    color: ${props => props.theme.colors.primary};
  }

  strong {
    color: #1a3a52;
    font-weight: 700;
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 2rem;
  align-items: stretch;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const OptionCard = styled.a`
  background: white;
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 10px 40px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  overflow: hidden;
  border: 2px solid transparent;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 20px 50px rgba(0,0,0,0.12);
  }

  ${props => props.$featured && `
    border-color: #1a3a52;
    background: linear-gradient(180deg, rgba(26, 58, 82, 0.03) 0%, white 15%);
    box-shadow: 0 12px 45px rgba(26, 58, 82, 0.15);

    &:hover {
      box-shadow: 0 20px 50px rgba(26, 58, 82, 0.2);
    }
  `}
`;

const RecommendedBadge = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  background: linear-gradient(135deg, #1a3a52 0%, #2c5f7f 100%);
  color: #f4d03f;
  padding: 0.5rem 1.25rem;
  font-size: 0.85rem;
  font-weight: 700;
  border-radius: 0 16px 0 12px;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border: 2px solid #f4d03f;
  border-top: 0;
  border-right: 0;
`;

const OptionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.85rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.75rem;
  padding-right: ${props => props.$hasBadge ? '120px' : '0'};
  line-height: 1.3;

  &::after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background: #1a3a52;
    margin-top: 0.5rem;
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 1.6rem;
    padding-right: 0;
  }
`;

const OptionSubtitle = styled.p`
  font-size: 1rem;
  color: #1a3a52;
  font-weight: 600;
  margin: 0 0 1rem;
`;

const OptionDescription = styled.div`
  font-size: 1.05rem;
  line-height: 1.75;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  flex-grow: 1;

  p {
    margin: 0 0 0.75rem;
  }

  ul {
    margin: 0.5rem 0;
    padding-left: 1.35rem;
  }

  li {
    margin: 0.4rem 0;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;

  li {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0.5rem 0;
    font-size: 1rem;
  }
`;

const ListIcon = styled(FontAwesomeIcon).attrs({ 'aria-hidden': true })`
  color: #1a3a52;
  width: 1rem;
  flex-shrink: 0;
`;

const InlineIcon = styled(FontAwesomeIcon).attrs({ 'aria-hidden': true })`
  color: #1a3a52;
  margin-right: 0.35rem;
`;

const CTAButton = styled.span`
  display: inline-block;
  background: linear-gradient(135deg, #1a3a52 0%, #2c5f7f 100%);
  color: #f4d03f;
  padding: 1rem 1.75rem;
  border-radius: 10px;
  font-weight: 700;
  font-size: 1.1rem;
  text-align: center;
  margin-top: auto;
  transition: all 0.25s ease;
  border: 2px solid #f4d03f;
  box-shadow: 0 4px 12px rgba(26, 58, 82, 0.3);

  ${OptionCard}:hover & {
    transform: scale(1.02);
    box-shadow: 0 6px 16px rgba(26, 58, 82, 0.4);
  }
`;

const CTAButtonSecondary = styled.span`
  display: inline-block;
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 1rem 1.75rem;
  border-radius: 10px;
  font-weight: 700;
  font-size: 1.1rem;
  text-align: center;
  margin-top: auto;
  transition: all 0.25s ease;

  ${OptionCard}:hover & {
    transform: scale(1.02);
  }
`;

const PirateHighlight = styled.div`
  background: linear-gradient(135deg, rgba(26, 58, 82, 0.08) 0%, rgba(26, 58, 82, 0.12) 100%);
  border-left: 4px solid #1a3a52;
  padding: 1rem 1.25rem;
  border-radius: 8px;
  margin: 1rem 0;
  font-size: 1rem;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
`;

const CircusHighlight = styled.div`
  background: linear-gradient(135deg, rgba(244, 208, 63, 0.15) 0%, rgba(244, 208, 63, 0.08) 100%);
  border-left: 4px solid #f4d03f;
  padding: 1rem 1.25rem;
  border-radius: 8px;
  margin: 1rem 0;
  font-size: 1rem;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  
  strong {
    color: #1a3a52;
  }
`;

const PirateTrail = () => {
  const castleEntryUrl = 'https://www.belvoircastle.com/booking-journey?locationId=978&pageId=b17520e4-20c1-451c-84ea-38c476c5fad7';
  const parklandUrl = 'https://www.belvoircastle.com/booking-journey?locationId=978&pageId=ccb57ad1-9af9-4bf8-8192-0b9d08f0c746';

  const meta = {
    title: '🏴‍☠️ Pirate Takeover at Belvoir Castle | Adventure Trail & Castle Entry',
    description: 'Join the Pirate Takeover at Belvoir Castle (23–31 May). Choose Pirate Adventure with Castle entry or Trail and Gardens. Book the Pirate-Themed Circus (23rd–25th May) and get 20% off your entry ticket — best value for a full family day out! Group Saver discount available.',
    canonical: 'https://www.belvoircastle.com/pirate-trail',
  };

  return (
    <Page>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta name="keywords" content="Pirate Takeover, Pirate Adventure Trail, Pirate Circus, Pirate-themed circus, Belvoir Castle, family day out, Adventure Playground, Castle entry, gardens, Group Saver, Circus discount, May half term, 23rd-25th May, Leicestershire" />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
      </Helmet>

      <EventHero image={heroImage}>
        <HeroContent>
          <Badge>🏴‍☠️ Pirate Takeover • 23–31 May</Badge>
          <Title>🏴‍☠️ Pirate Adventure Trail</Title>
          <Strapline>Set sail for adventure this May half term — choose Pirate Adventure with Castle or Trail &amp; Gardens</Strapline>
        </HeroContent>
      </EventHero>

      <Content>
        <IntroSection>
          <IntroText>
            Ahoy, mateys! Join us for the <strong>🏴‍☠️ Pirate Takeover</strong> (23–31 May). Our Pirate Adventure Trail is in the <strong>Adventure Playground</strong>. Choose <strong>The Ultimate Pirate Adventure</strong> for a full day out — Castle, gardens and pirate fun — or <strong>Pirate Trail and Gardens</strong> for the trail plus gardens access.
          </IntroText>
        </IntroSection>

        <CircusBonus>
          <p><strong>🎪 🏴‍☠️ PIRATE-THEMED CIRCUS • 23rd–25th May</strong></p>
          <p style={{ marginTop: '0.75rem' }}>Book the Pirate-themed Circus on <strong>23rd, 24th, or 25th May</strong>! This special pirate circus can be added as part of your Pirate Adventure Trail booking. <strong>Get 20% off your entry ticket to the Castle or Grounds</strong> when you book with a Circus ticket — the best value for a full family day out! Select dates 23rd–25th May when booking.</p>
        </CircusBonus>

        <GroupSaverBox>
          <p><strong>⚓ Enjoy the Belvoir Group Saver discount</strong> — the more you add, the more you save. Bring your crew and make the most of your pirate adventure!</p>
        </GroupSaverBox>

        <CardsGrid>
          <OptionCard href={castleEntryUrl} target="_blank" rel="noopener noreferrer" $featured>
            <RecommendedBadge><FontAwesomeIcon icon={faStar} /> Best value</RecommendedBadge>
            <OptionTitle $hasBadge>🏴‍☠️ The Ultimate Pirate Adventure – Castle & Gardens</OptionTitle>
            <OptionSubtitle>Everything included for your best pirate day out</OptionSubtitle>
            <OptionDescription>
              <p>Make the most of the Pirate Takeover at Belvoir with our full-day experience. Follow the Pirate Adventure Trail in the Adventure Playground, then explore the Castle and its treasures.</p>
              <PirateHighlight>
                <InlineIcon icon={faPaw} /> Includes Castle entry &amp; the Bertie the Bear Trail!
              </PirateHighlight>
              <FeatureList>
                <li><ListIcon icon={faAnchor} /> 🏴‍☠️ Pirate Adventure Trail in the Adventure Playground</li>
                <li><ListIcon icon={faLandmark} /> Castle entry &amp; Bertie the Bear Trail</li>
                <li><ListIcon icon={faTree} /> Gardens and parkland</li>
                <li><ListIcon icon={faClock} /> Full day out — best value</li>
                <li><ListIcon icon={faPaw} /> Free Animal Park included</li>
              </FeatureList>
              <PirateHighlight>
                Free Animal Park — included with your Castle ticket!
              </PirateHighlight>
              <CircusHighlight>
                <strong>🎪 🏴‍☠️ Add the Pirate-Themed Circus for 20% off!</strong> Book a Circus ticket (23rd–25th May) and get 20% off your Castle entry — best value for a full family day out!
              </CircusHighlight>
            </OptionDescription>
            <CTAButton>🏴‍☠️ Book the Ultimate Adventure →</CTAButton>
          </OptionCard>

          <OptionCard href={parklandUrl} target="_blank" rel="noopener noreferrer">
            <OptionTitle>🏴‍☠️ Pirate Trail and Gardens</OptionTitle>
            <OptionSubtitle>Trail in the playground + gardens access</OptionSubtitle>
            <OptionDescription>
              <p>Follow the Pirate Adventure Trail in the Adventure Playground and enjoy access to the gardens too. Perfect if you're after a shorter visit — the trail is in the playground and you can explore the beautiful gardens as well.</p>
              <FeatureList>
                <li><ListIcon icon={faAnchor} /> 🏴‍☠️ Pirate Adventure Trail in the Adventure Playground</li>
                <li><ListIcon icon={faTree} /> Gardens access included</li>
                <li><ListIcon icon={faSun} /> Great for a May half term adventure</li>
              </FeatureList>
              <CircusHighlight>
                <strong>🎪 🏴‍☠️ Add the Pirate-Themed Circus for 20% off!</strong> Book a Circus ticket (23rd–25th May) and get 20% off your Grounds entry — best value for a full family day out!
              </CircusHighlight>
            </OptionDescription>
            <CTAButtonSecondary>⚓ Book Trail &amp; Gardens →</CTAButtonSecondary>
          </OptionCard>
        </CardsGrid>
      </Content>

      <Footer />
    </Page>
  );
};

export default PirateTrail;
